import { useEffect } from 'react'
import { useAuth } from '@/auth/useAuth'
import { useDebtStore } from '@/store/debtStore'
import { useLedgerStore } from '@/store/ledgerStore'
import { useRemoteRefreshStore } from '@/store/remoteRefreshStore'

/** Minimum gap between background refreshes. */
export const REFRESH_MIN_INTERVAL_MS = 30_000
/**
 * v0.8.57 — also refresh on a timer while the app stays in front (a laptop
 * left open on the Dashboard never fires focus/visibility events).
 */
export const REFRESH_POLL_MS = 120_000

/**
 * True when refreshing now could disturb the user: a dialog is open (it may
 * hold unsaved input) or focus is in a form field.
 */
export function isUserEditing(doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"], [aria-modal="true"]')) return true
  const el = doc.activeElement
  return !!el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

/** Re-reads everything a household member may have changed. */
export function refreshSharedData(): void {
  void useDebtStore.getState().refresh()
  void useLedgerStore.getState().refresh()
  useRemoteRefreshStore.getState().bump()
}

/**
 * v0.8.56 — household members share one plan, but each device only loaded
 * it once. When the app comes back to the foreground (tab focus, phone
 * unlock) after ≥ 30 s — and, since v0.8.57, every 2 minutes while it stays
 * in front — quietly re-read debts, the monthly plan and the recovery
 * anchor so the other person's changes show up. Cloud sessions only: a
 * local build is one device. The stores themselves skip the refresh if any
 * save is in flight.
 */
export function useRefreshOnFocus(): void {
  const { isAuthenticated, workspaceId } = useAuth()

  useEffect(() => {
    if (!isAuthenticated || !workspaceId) return
    let last = Date.now()

    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - last < REFRESH_MIN_INTERVAL_MS || isUserEditing()) return
      last = Date.now()
      refreshSharedData()
    }

    document.addEventListener('visibilitychange', maybeRefresh)
    window.addEventListener('focus', maybeRefresh)
    const poll = setInterval(maybeRefresh, REFRESH_POLL_MS)
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh)
      window.removeEventListener('focus', maybeRefresh)
      clearInterval(poll)
    }
  }, [isAuthenticated, workspaceId])
}
